import React, { useEffect, useState, memo } from 'react';
import { Row, Col, Card, ListGroup, Badge, Container, Spinner, Image } from 'react-bootstrap';
import { defaultMapProps, mapApiKey, getStillageHistoryDetails } from "services/stillageDashboard";
import { toast } from 'react-toastify';
import FalconLightBoxGallery from 'components/common/FalconLightBoxGallery';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import PropTypes from "prop-types";
import MapWrapper from '../../stillage-map/MapWrapper';
import { MapIcon } from '../../stillage-map/MapIcon';
import GoogleMapReact from 'google-map-react';

const StillageHistory = ({ showModal, stillageNo }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!showModal || !stillageNo) {
            setData([]);
            setLoading(false);
            return;
        }

        // Captured locally so a superseded request can tell it is no longer the current one
        const controller = new AbortController();
        setLoading(true);

        getStillageHistoryDetails(stillageNo, controller)
            .then(data => {
                if (controller.signal.aborted) return;
                setData(data ?? []);
            })
            .catch(error => {
                if (controller.signal.aborted) return;
                setData([]);
                toast.error(`Error fetching history data: ${error.message}`);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [showModal, stillageNo]);

    if (loading)
        return (
            <div className='justify-content-center d-flex w-100 my-5'>
                <Spinner animation="border" role="status" variant='primary' />
            </div>
        );

    if (!data?.length && !loading)
        return (
            <div className='h-100 text-center align-middle my-4'>No data</div>
        );

    return (
        <>
            {data.map((item, index) => {
                // All attachments are images or PDF files for certificates
                const photos = item.photos?.filter(x => !x.path?.toLowerCase().includes('pdf'));
                const files = item.photos?.filter(x => x.path?.toLowerCase().includes('pdf'));
                return (
                    <Row key={`${item.stillageID}_${index}`}>
                        <Col className='d-flex justify-content-start ps-2' sm={6}>
                            <ListGroup className='w-100' variant="flush">
                                <Card className='m-2 p-1'>
                                    <Card.Title className='p-2'>
                                        <Badge className='me-1 text-light' bg="dark">{item.statusName}</Badge>
                                        <Badge bg="secondary">{dayjs(item.statusDateTime).format('DD/MM/YYYY')}</Badge>
                                    </Card.Title>
                                    <Card.Body>
                                        <ListGroup variant="flush">
                                            <ListGroup.Item>
                                                <Row>
                                                    <Col className='d-flex justify-content-start'>
                                                        <strong>Customer Name:</strong>
                                                    </Col>
                                                    <Col className='d-flex justify-content-end'>
                                                        {item.customerName || 'N/A'}
                                                    </Col>
                                                </Row>
                                            </ListGroup.Item>
                                            <ListGroup.Item>
                                                <Row>
                                                    <Col className='d-flex justify-content-start'>
                                                        <strong>Stillage ID:</strong>
                                                    </Col>
                                                    <Col className='d-flex justify-content-end'>
                                                        {item.fbStillageID}
                                                    </Col>
                                                </Row>
                                            </ListGroup.Item>
                                            <ListGroup.Item>
                                                <Row>
                                                    <Col className='d-flex justify-content-start'>
                                                        <strong>Notes:</strong>
                                                    </Col>
                                                    <Col className='d-flex justify-content-end'>
                                                        {item.notes ?? 'N/A'}
                                                    </Col>
                                                </Row>
                                            </ListGroup.Item>
                                            {item.statusName === 'Certified' ?
                                                <>
                                                    <ListGroup.Item>
                                                        <Row>
                                                            <Col className='d-flex justify-content-start'>
                                                                <strong>Certificate Start:</strong>
                                                            </Col>
                                                            <Col className='d-flex justify-content-end'>
                                                                {item.certStartDate ? dayjs(item.CertStartDate).format('DD/MM/YYYY') : 'N/A'}
                                                            </Col>
                                                        </Row>
                                                    </ListGroup.Item>
                                                    <ListGroup.Item>
                                                        <Row>
                                                            <Col className='d-flex justify-content-start'>
                                                                <strong>Certificate End:</strong>
                                                            </Col>
                                                            <Col className='d-flex justify-content-end'>
                                                                {item.certEndDate ? dayjs(item.CertEndDate).format('DD/MM/YYYY') : 'N/A'}
                                                            </Col>
                                                        </Row>
                                                    </ListGroup.Item>
                                                    <ListGroup.Item>
                                                        <Row>
                                                            <Col className='d-flex justify-content-start'>
                                                                <strong>Uploaded By:</strong>
                                                            </Col>
                                                            <Col className='d-flex justify-content-end'>
                                                                {item.uploadedBy ?? 'N/A'}
                                                            </Col>
                                                        </Row>
                                                    </ListGroup.Item>
                                                </> :
                                                <ListGroup.Item>
                                                    <Row>
                                                        <Col sm={2} className='d-flex justify-content-start'>
                                                            <strong>Map:</strong>
                                                        </Col>
                                                        <Col className='d-flex justify-content-end'>
                                                            {item.long && item.lat && showModal ?
                                                                <MapWrapper height={'200px'} width={'100%'}>
                                                                    <GoogleMapReact
                                                                        bootstrapURLKeys={{ key: mapApiKey }}
                                                                        defaultCenter={{
                                                                            lng: item.long ?? defaultMapProps.center.lng,
                                                                            lat: item.lat ?? defaultMapProps.center.lat
                                                                        }}
                                                                        defaultZoom={defaultMapProps.zoom + 6}
                                                                    >
                                                                        <MapIcon
                                                                            lat={item.lat}
                                                                            lng={item.long}
                                                                        />
                                                                    </GoogleMapReact>
                                                                </MapWrapper>
                                                                :
                                                                'No location data available'
                                                            }
                                                        </Col>
                                                    </Row>
                                                </ListGroup.Item>
                                            }
                                        </ListGroup>
                                    </Card.Body>
                                </Card>
                            </ListGroup>
                        </Col>
                        <Col className='d-flex justify-content-end ps-2' sm={6}>
                            {photos && photos.length > 0 && showModal ? (
                                <FalconLightBoxGallery images={photos.map(x => x.path)}>
                                    {setImgIndex => (
                                        <Container>
                                            <Row>
                                                {photos.map((photo, idx) => (
                                                    <Col sm={4} key={photo.path ?? idx}>
                                                        <div className='shadow m-2'>
                                                            <Image
                                                                fluid
                                                                loading='lazy'
                                                                rounded
                                                                src={photo.path}
                                                                alt={`Stillage Photo ${idx + 1}`}
                                                                className="d-block position-relative"
                                                                onClick={() => setImgIndex(idx)}
                                                            // onError={err => console.log(err)}
                                                            />
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </Container>
                                    )}
                                </FalconLightBoxGallery>
                            ) : <></>}
                            {files && files.length > 0 && showModal ?
                                <Container>
                                    <Row className='h-100'>
                                        {files.map((file, idx) => (
                                            <>
                                                <div className='p-0 position-absolute end-0'>
                                                    <a href={file.path} target='_blank' rel="noreferrer">
                                                        <FontAwesomeIcon icon="eye" className="text-600 float-end m-2" />
                                                    </a>
                                                </div>
                                                <Col sm={12} key={file.path ?? idx} className='h-100'>
                                                    <div className='shadow m-0 h-100'>
                                                        <iframe src={file.path} className="w-100 h-100 rounded" />
                                                    </div>
                                                </Col>
                                            </>
                                        ))}
                                    </Row>
                                </Container> :
                                <></>
                            }
                        </Col>
                        {index < data.length - 1 && <div className='d-flex justify-content-center'><hr className='my-3 text-black-25 w-75' /></div>}
                    </Row>
                )
            })}
        </>);
}

StillageHistory.propTypes = {
    showModal: PropTypes.bool,
    modalData: PropTypes.array,
    stillageNo: PropTypes.number,
    loading: PropTypes.bool
};

export default memo(StillageHistory);